import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../common/Button';
import { TextInput } from '../common/TextInput';
import { SearchSelect } from '../common/SearchSelect/SearchSelect';
import {Modal} from '../common/Modal';
import { useRequestManager } from '../common/RequestManager';
import { LINK_CARTON_BOX_TO_VARIATION, UPDATE_CARTON_BOX_VARIATION_LINK } from './RestockShipments.queries';
import { GetRestockShipmentQuery, LinkCartonBoxToVariationMutation, LinkCartonBoxToVariationMutationVariables, UpdateCartonBoxVariationLinkMutation, UpdateCartonBoxVariationLinkMutationVariables } from '../../../codegen/graphql';
import { getTranslatedValue } from '../../../common/getTranslatedValue';

interface LinkVariationToBoxModalProps {
  boxId: string;
  shipmentProducts: NonNullable<GetRestockShipmentQuery['restockShipment']>['products'];
  cartonBoxes: NonNullable<GetRestockShipmentQuery['restockShipment']>['cartonBoxes'];
  onClose: () => void;
  onLink: () => void;
  editingVariation?: NonNullable<GetRestockShipmentQuery['restockShipment']>['products'][number]['variations'][number]; // For editing existing links
  editingLink?: NonNullable<GetRestockShipmentQuery['restockShipment']>['cartonBoxes'][number]['linkedProductVariations'][number];
}

type ShipmentVariationOption = NonNullable<GetRestockShipmentQuery['restockShipment']>['products'][number]['variations'][number] & {
  productName: string;
};

const LinkVariationToBoxModal: React.FC<LinkVariationToBoxModalProps> = ({
  boxId,
  shipmentProducts,
  cartonBoxes,
  onClose,
  onLink,
  editingVariation,
  editingLink,
}) => {
  const { trackRequest } = useRequestManager();
  const [selectedVariation, setSelectedVariation] = useState<ShipmentVariationOption | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [boxFillCoeff, setBoxFillCoeff] = useState('');

  const [linkVariation] = useMutation<
    LinkCartonBoxToVariationMutation,
    LinkCartonBoxToVariationMutationVariables
  >(LINK_CARTON_BOX_TO_VARIATION);
  const [updateVariationLink] = useMutation<
    UpdateCartonBoxVariationLinkMutation,
    UpdateCartonBoxVariationLinkMutationVariables
  >(UPDATE_CARTON_BOX_VARIATION_LINK);

  // Initialize form with editing data if provided
  useEffect(() => {
    if (editingVariation || editingLink) {
      if (editingVariation) {
        const product = shipmentProducts.find((p) =>
          p.variations?.some((v) => v.id === editingVariation.id)
        );
        setSelectedVariation({
          ...editingVariation,
          productName: product?.supplierProduct?.name || 'Unknown Product',
        });
      } else {
        setSelectedVariation(null);
      }
      setQuantity(editingLink?.quantity?.toString() || '1');
      setBoxFillCoeff(editingLink?.boxFillCoeff?.toString() || '');
    }
  }, [editingVariation, editingLink, shipmentProducts]);

  // Calculate received quantity for a variation (sum of all quantities linked to boxes)
  const calculateReceivedQuantity = (
    variation: NonNullable<GetRestockShipmentQuery['restockShipment']>['products'][number]['variations'][number]
  ): number => {
    let receivedQuantity = 0;
    
    cartonBoxes.forEach(box => {
      if (box.linkedProductVariations) {
        box.linkedProductVariations.forEach((link: any) => {
          if (link.restockShipmentProductVariationId === variation.id) {
            receivedQuantity += link.quantity || 0;
          }
        });
      }
    });
    
    return receivedQuantity;
  };

  // Get all variations and filter to only show those with missing quantity
  const allVariations = shipmentProducts.flatMap((product) =>
    (product.variations || []).map((variation: any) => ({
      ...variation,
      productName: product.supplierProduct?.name || 'Unknown Product',
    }))
  );

  // Filter variations to only show those with missing quantity (not fully added to boxes)
  // When editing, always include the variation being edited
  const variationsWithMissingQuantity = allVariations.filter((variation) => {
    // Always include the variation being edited
    if (editingVariation && variation.id === editingVariation.id) {
      return true;
    }
    
    const receivedQuantity = calculateReceivedQuantity(variation);
    const missingQuantity = variation.quantity - receivedQuantity;
    
    // Only show variations that have missing quantity (more than 0)
    return missingQuantity > 0;
  });

  const getVariationLabel = (variation: ShipmentVariationOption): string => {
    const receivedQuantity = calculateReceivedQuantity(variation);
    const missingQuantity = variation.quantity - receivedQuantity;
    const typeValues = variation.supplierProductVariation?.typeValues
      ?.map((tv: any) => getTranslatedValue(tv.value))
      .join(' / ') || 'Variation';

    return `${variation.productName} - ${typeValues} (Missing: ${missingQuantity} / Total: ${variation.quantity})`;
  };

  const loadVariationOptions = useCallback((searchText: string) => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return variationsWithMissingQuantity;
    }

    return variationsWithMissingQuantity.filter((variation) =>
      getVariationLabel(variation).toLowerCase().includes(normalizedSearch)
    );
  }, [variationsWithMissingQuantity]);

  const handleLink = async () => {
    if (!selectedVariation) {
      alert('Please select a variation');
      return;
    }

    if (editingVariation) {
      // Update existing link
      await trackRequest(
        updateVariationLink({
          variables: {
            cartonBoxId: boxId,
            variationId: selectedVariation.id || '',
            input: {
              quantity: parseInt(quantity),
              boxFillCoeff: boxFillCoeff ? parseFloat(boxFillCoeff) : undefined,
            },
          },
        }),
        { title: 'Updating variation link' }
      );
    } else {
      // Create new link
      await trackRequest(
        linkVariation({
          variables: {
            input: {
              restockShipmentCartonBoxId: boxId,
              restockShipmentProductVariationId: selectedVariation.id,
              supplierProductId: selectedVariation.supplierProductVariation!.supplierProductId,
              supplierVariationSku: selectedVariation.supplierProductVariation!.sku,
              quantity: parseInt(quantity),
              boxFillCoeff: boxFillCoeff ? parseFloat(boxFillCoeff) : null,
            },
          },
        }),
        { title: 'Linking variation to box' }
      );
    }
    onLink();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLink();
  };

  return (
    <Modal onClose={onClose} title={editingVariation ? "Edit Variation Link" : "Link Variation to Box"}>
      <form className="p-6" onSubmit={handleSubmit}>
        <div className="mb-4">
          <SearchSelect<ShipmentVariationOption>
            label="Select Variation"
            placeholder="-- Select a variation --"
            value={selectedVariation}
            loadOptions={loadVariationOptions}
            onSelect={setSelectedVariation}
            onClear={() => setSelectedVariation(null)}
            disabled={!!editingVariation}
            allowDelete={!editingVariation}
            renderOption={(variation, selectCallback, isSelected) => (
              <div
                key={variation.id}
                className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${isSelected ? 'bg-blue-50' : ''}`}
                onClick={selectCallback}
              >
                {getVariationLabel(variation)}
              </div>
            )}
            renderSelected={(variation) => getVariationLabel(variation)}
          />
        </div>

        <div className="mb-4">
          <TextInput
            label="Quantity in this Box"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            min="1"
          />
        </div>

        <div className="mb-4">
          <TextInput
            label="Box Fill Coefficient (optional)"
            type="number"
            value={boxFillCoeff}
            onChange={(e) => setBoxFillCoeff(e.target.value)}
            placeholder="e.g., 0.85"
            step="0.01"
          />
          <p className="text-xs text-gray-500 mt-1">
            Coefficient indicating how much of the box this variation fills (0-1)
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" preset="white" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            {editingVariation ? 'Update Link' : 'Link Variation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default LinkVariationToBoxModal;

